import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { getAssessment } from "@/data/learn/assessments";
import { AssessmentRunner } from "@/components/learn/AssessmentRunner";
import { gatedRobotsMeta } from "@/lib/seo-gating";


export const Route = createFileRoute("/learn/assessments/$slug")({
  loader: ({ params }) => {
    const assessment = getAssessment(params.slug);
    if (!assessment) throw notFound();
    return { assessment };
  },
  head: ({ loaderData }) => {
    if (!loaderData) {
      return {
        meta: [{ title: "Not found — Recovery Trauma™" }, { name: "robots", content: "noindex" }],
      };
    }
    const { assessment } = loaderData;
    const path = `/learn/assessments/${assessment.slug}`;
    const url = `https://recoverytrauma.com${path}`;
    const description = `${assessment.tagline} — a short educational self-assessment from Recovery Trauma™. Reflection only, never a diagnosis.`;
    return {
      meta: [
        { title: `${assessment.title} Self-Assessment — Recovery Trauma™` },
        { name: "description", content: description },
        { property: "og:title", content: `${assessment.title} Self-Assessment` },
        { property: "og:description", content: description },
        { property: "og:type", content: "article" },
        { property: "og:url", content: url },
        { name: "twitter:card", content: "summary_large_image" },
        ...gatedRobotsMeta(path),
      ],
      links: [{ rel: "canonical", href: url }],
    };

  },
  notFoundComponent: AssessmentNotFound,
  component: AssessmentPage,
});

function AssessmentNotFound() {
  return (
    <main className="mx-auto max-w-3xl px-4 py-16 text-center">
      <h1 className="font-serif text-2xl text-foreground">We couldn&apos;t find that assessment</h1>
      <Link to="/learn/assessments" className="mt-4 inline-block text-gold hover:underline">
        Back to Self-Assessments
      </Link>
    </main>
  );
}

function AssessmentPage() {
  const { assessment } = Route.useLoaderData();

  return (
    <main className="mx-auto max-w-3xl px-4 py-10">
      <nav className="mb-6 text-sm text-muted-foreground">
        <Link to="/learn" className="hover:text-foreground">
          Learn
        </Link>{" "}
        /{" "}
        <Link to="/learn/assessments" className="hover:text-foreground">
          Self-Assessments
        </Link>
      </nav>

      <header className="mb-6">
        <span className="text-3xl" aria-hidden="true">
          {assessment.emoji}
        </span>
        <h1 className="mt-2 font-serif text-3xl tracking-tight text-foreground md:text-4xl">
          {assessment.title}
        </h1>
        <p className="mt-2 text-base text-muted-foreground">{assessment.tagline}</p>
      </header>

      <AssessmentRunner assessment={assessment} />

      <div className="mt-10 text-center">
        <Link
          to="/learn/assessments"
          className="text-sm text-muted-foreground underline-offset-4 hover:text-foreground hover:underline"
        >
          ← All self-assessments
        </Link>
      </div>
    </main>
  );
}
